import {
	transformBaseImageProps,
	transformBaseSourceProps,
	type CoreImageAttributes,
	type CoreSourceAttributes,
} from '@unpic/core/base';
import { generateImageSrc, getRuntimeImgproxyUrl } from '@/lib/shared';
import { cn } from '@worldcoo/ui/lib/utils';
import { useRouteContext } from '@tanstack/react-router';
import type { ComponentProps } from 'react';

const FIXED_ATTR_MAP: Record<string, string> = {
	srcset: 'srcSet',
	fetchpriority: 'fetchPriority',
};

function camelizeAttr(key: string): string {
	if (key.startsWith('data-') || key.startsWith('aria-')) return key;
	return FIXED_ATTR_MAP[key] ?? key.replace(/-./g, (s) => s[1]!.toUpperCase());
}

// Unpic joins srcset entries with ",\n".
// Under Bun's production SSR streaming pipeline, literal newlines inside attribute
// values get mangled — only the first entry survives and the rest leak as text
// between <source> and <img>. Replace with ", " (valid srcset syntax).
//
// We also drop a few attrs that Unpic emits but we don't need:
//  - style: max-width/max-height/object-fit/width:100% all duplicate Tailwind
//    classes set by callers; the only load-bearing piece is aspect-ratio, which
//    we re-emit in a minimal style below for CLS protection.
//  - role="presentation": alt="" already implies decorative.
//  - srcset/sizes (img only, when AVIF source is present): the <source> covers
//    AVIF-capable browsers (>=96% globally); the <img> only needs `src` as fallback.
function toReactProps(props: object, opts: { dropSrcSet?: boolean } = {}): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(props)) {
		if (key === 'style') continue;
		if (key === 'role' && value === 'presentation') continue;
		// We set width/height explicitly on the element — unpic's constrained layout
		// returns undefined for these which would override our values via spread.
		if (key === 'width' || key === 'height') continue;
		if (opts.dropSrcSet && (key === 'srcset' || key === 'sizes')) continue;
		const reactKey = camelizeAttr(key);
		out[reactKey] = reactKey === 'srcSet' && typeof value === 'string' ? value.replace(/,\n/g, ', ') : value;
	}
	return out;
}

/**
 * Anchor breakpoints on the actual display width: 1×, 1.5× and 2× cover all
 * device pixel ratios in use today with ≥25% steps between variants. 160 and
 * 320 are kept as mobile-fallback floors. This trims the srcset from 10–11
 * entries down to 4–5 per image, halving the bytes and the CDN cache surface.
 */
function getBreakpoints(width: number): number[] {
	const candidates = [160, 320, width, Math.round(width * 1.5), width * 2].filter((w) => w <= width * 2);
	// There is not a wide support for toSorted() in the browser
	// oxlint-disable-next-line unicorn/no-array-sort
	return [...new Set(candidates)].sort((a, b) => a - b);
}

/** Generate a stepped `sizes` from breakpoints so the browser doesn't assume 100vw. */
function getSizes(width: number, breakpoints: number[]): string {
	// There is not a wide support for toSorted() in the browser
	// oxlint-disable-next-line unicorn/no-array-sort
	const steps = [...new Set(breakpoints)].filter((bp) => bp <= width).sort((a, b) => b - a);
	return steps.map((bp, i) => (i === steps.length - 1 ? `${bp}px` : `(min-width: ${bp}px) ${bp}px`)).join(', ');
}

export function Image(props: {
	src: string | undefined;
	height: number;
	aspectRatio: number;
	objectFit?: 'contain' | 'cover';
	alt?: string;
	/** When true, sets fetchPriority="high" and loading="eager". Default is lazy. */
	priority?: boolean;
	className?: string;
}) {
	const { baseUrl } = useRouteContext({ from: '__root__' });

	if (!props.src) {
		return <div className={cn(props.className, 'bg-transparent')} style={{ aspectRatio: props.aspectRatio }} />;
	}

	let src = props.src;
	if (props.src.startsWith('/')) {
		try {
			src = new URL(props.src, baseUrl).href;
		} catch {
			// baseUrl is somehow invalid – fall back to the relative path and let the browser resolve it
		}
	}

	const resizeType = (props.objectFit ?? 'cover') === 'contain' ? 'fit' : 'fill';
	const transformer = (src: string | URL, opts: { height?: number; width?: number; format?: 'webp' | 'avif' }) =>
		generateImageSrc(src.toString(), {
			height: opts.height,
			width: opts.width,
			format: opts.format,
			quality: opts.format === 'avif' ? 60 : 70,
			resizeType,
		});

	const hasImgproxy = !!getRuntimeImgproxyUrl();
	const width = Math.round(props.height * props.aspectRatio);
	const breakpoints = getBreakpoints(width);
	const sizes = getSizes(width, breakpoints);

	const sourceAttrs = hasImgproxy
		? toReactProps(
				transformBaseSourceProps<CoreSourceAttributes, { width?: number; height?: number }, unknown>({
					src,
					layout: 'constrained',
					width,
					height: props.height,
					type: 'image/avif',
					priority: props.priority,
					sizes,
					breakpoints,
					transformer: (src: string | URL, opts: { height?: number; width?: number }) =>
						transformer(src, { height: opts.height, width: opts.width, format: 'avif' }),
				}),
			)
		: undefined;

	const imgAttrs = toReactProps(
		transformBaseImageProps<
			{ width?: number; height?: number; format?: 'webp' | 'avif' },
			unknown,
			CoreImageAttributes
		>({
			src,
			layout: 'constrained',
			width,
			height: props.height,
			objectFit: props.objectFit ?? 'cover',
			alt: props.alt,
			priority: props.priority,
			sizes,
			breakpoints,
			transformer,
		}),
		// When the AVIF <source> is present it covers responsive selection for all
		// modern browsers; the <img> only needs a single `src` as a legacy fallback.
		{ dropSrcSet: !!sourceAttrs },
	);

	// Round to 3 decimals — enough precision for layout, much shorter than the
	// raw float Unpic emits (e.g. 1.4549653579676673 → 1.455).
	const aspectRatio = Math.round(props.aspectRatio * 1000) / 1000;

	return (
		<picture>
			{sourceAttrs && (
				<source
					width={width}
					height={props.height}
					className={props.className}
					{...(sourceAttrs as ComponentProps<'source'>)}
					style={{ aspectRatio }}
				/>
			)}
			{/* width/height are stripped from unpic output (see toReactProps) and set
		    explicitly here for CLS prevention — unpic's constrained layout does
		    not emit them. */}
			<img
				width={width}
				height={props.height}
				className={props.className}
				{...(imgAttrs as ComponentProps<'img'>)}
				style={{ aspectRatio }}
			/>
		</picture>
	);
}
